import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Plus,
    Clock,
    CheckCircle,
    XCircle,
    AlertCircle,
    FileText,
    DollarSign,
    Calendar,
    Upload,
    X,
    Receipt,
    Loader,
    MapPin,
    Briefcase,
} from "lucide-react";
import Swal from "sweetalert2";
import {
    getMyNoteDeFraisRequestsRequest,
    createNoteDeFraisRequestRequest,
} from "../../../../redux/actions/noteDeFraisRequestActions";
import { fetchNoteDeFraisRequest } from "../../../../redux/actions/noteDeFraisActions";
import { fetchOrdreMissionActifsRequest } from "../../../../redux/actions/ordreMissionActions";
import "../../parametres/parametresStyles/ParamConges.css";

const ExpenseManagement = () => {
    const dispatch = useDispatch();

    // Redux state
    const { myRequests, loading } = useSelector((state) => state.noteDeFraisRequests || { myRequests: [], loading: false });
    const { notesDeFrais } = useSelector((state) => state.noteDeFrais || { notesDeFrais: [] });
    const { ordresMissionActifs } = useSelector((state) => state.ordreMission || { ordresMissionActifs: [] });
    const user = useSelector((state) => state.auth.user);

    const [showModal, setShowModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);

    const [formData, setFormData] = useState({
        ordreMissionId: "",
        noteDeFraisId: "",
        category: "",
        dateDepense: "",
        montant: "",
        description: "",
        centreCouts: "",
        urgence: false,
        justificatifs: [],
    });

    // Charger les données
    useEffect(() => {
        dispatch(fetchNoteDeFraisRequest());
        dispatch(getMyNoteDeFraisRequestsRequest());
        dispatch(fetchOrdreMissionActifsRequest());
    }, [dispatch]);

    const getCouleurByCategorie = (categorie) => {
        const colors = {
            TRANSPORT: "#3b82f6",
            HEBERGEMENT: "#8b5cf6",
            RESTAURATION: "#10b981",
            AUTRE: "#f59e0b",
        };
        return colors[categorie] || "#6b7280";
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        const d = new Date(dateStr);
        return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
    };

    const formatMontant = (montant) => {
        if (montant === null || montant === undefined || isNaN(montant)) return "0";
        return new Intl.NumberFormat("fr-MA", { style: "decimal", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(montant);
    };

    const getWorkflowLabel = (workflow) => {
        const labels = { manager: "Validation Manager", admin: "Validation RH", both: "Manager + RH" };
        return labels[workflow] || workflow;
    };

    const getRequestCategory = (demande) => demande?.category || demande?.noteDeFrais?.category || demande?.requestType || "-";
    const getRequestCode = (demande) => demande?.code || demande?.noteDeFrais?.code || "";

    const selectedOrdreMission = useMemo(() => {
        if (!formData.ordreMissionId) return null;
        return ordresMissionActifs.find(om => om.id === Number(formData.ordreMissionId)) || null;
    }, [formData.ordreMissionId, ordresMissionActifs]);

    const typesNoteDeFraisDisponibles = useMemo(() => {
        if (selectedOrdreMission) {
            // Filtrer selon la mission
            return selectedOrdreMission.notesDeFraisAutorisees || [];
        }
        // Sinon retourner tous les actifs
        return notesDeFrais.filter(ndf => ndf.actif);
    }, [selectedOrdreMission, notesDeFrais]);

    const maxDateDepense = useMemo(() => {
        const today = new Date().toISOString().split('T')[0];
        if (!selectedOrdreMission?.dateFin) return today;
        return selectedOrdreMission.dateFin < today ? selectedOrdreMission.dateFin : today;
    }, [selectedOrdreMission]);

    const getStatutBadge = (statut, demande = null) => {
        const d = demande || selectedDemande;
        if (statut === "APPROVED" && d?.statutRemboursement === "EN_ATTENTE") {
            return <span className="pcPill" style={{ color: '#0284c7', backgroundColor: '#e0f2fe', fontWeight: 700 }}>En attente de remboursement</span>;
        }

        const config = {
            PENDING_MANAGER: { label: "Attente Manager", cls: "pcPill is-off", color: "#f59e0b" },
            PENDING_ADMIN: { label: "Attente RH", cls: "pcPill is-off", color: "#f59e0b" },
            APPROVED: { label: "Approuvé", cls: "pcPill is-on", color: "#16a34a" },
            REJECTED: { label: "Rejeté", cls: "pcPill is-off", color: "#ef4444" },
            PAID: { label: "Remboursé", cls: "pcPill is-on", color: "#059669" },
            CANCELLED: { label: "Annulé", cls: "pcPill is-off", color: "#6b7280" },
        };

        const c = config[statut] || { label: statut, cls: "pcPill", color: "#6b7280" };
        return <span className={c.cls} style={{ color: c.color, backgroundColor: `${c.color}15`, fontWeight: 700 }}>{c.label}</span>;
    };

    const handleOrdreMissionChange = (e) => {
        const ordreMissionId = e.target.value;
        const mission = ordresMissionActifs.find((om) => om.id === Number(ordreMissionId));
        setFormData({
            ...formData,
            ordreMissionId,
            noteDeFraisId: "", // reset type de frais car il dépend de la mission
            category: "",
            centreCouts: mission?.centreCout || formData.centreCouts,
            dateDepense: mission && (!formData.dateDepense || formData.dateDepense < mission.dateDebut || formData.dateDepense > mission.dateFin)
                ? mission.dateDebut
                : formData.dateDepense,
        });
    };

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files || []);
        setFormData((p) => ({ ...p, justificatifs: [...p.justificatifs, ...files] }));
    };

    const removeFile = (index) => {
        setFormData((p) => ({ ...p, justificatifs: p.justificatifs.filter((_, i) => i !== index) }));
    };

    const resetForm = () => {
        setFormData({
            ordreMissionId: "",
            noteDeFraisId: "",
            category: "",
            dateDepense: "",
            montant: "",
            description: "",
            centreCouts: "",
            urgence: false,
            justificatifs: [],
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const type = typesNoteDeFraisDisponibles.find((t) => t.id === Number(formData.noteDeFraisId));
        if (!type) {
            Swal.fire({ icon: "error", title: "Erreur", text: "Veuillez sélectionner un type de frais." });
            return;
        }

        const montant = Number(formData.montant);

        if (type.montantMinimum && montant < type.montantMinimum) {
            Swal.fire({ icon: "error", title: "Erreur", text: `Le montant minimum pour ce type de frais est de ${formatMontant(type.montantMinimum)} ${type.devise || "MAD"}` });
            return;
        }
        if (type.montantMaximum && montant > type.montantMaximum) {
            Swal.fire({ icon: "error", title: "Erreur", text: `Le montant maximum autorisé pour ce type de frais est de ${formatMontant(type.montantMaximum)} ${type.devise || "MAD"}` });
            return;
        }

        if (type.obligatoire && formData.justificatifs.length === 0) {
            Swal.fire({ icon: "error", title: "Erreur", text: `Un justificatif de type ${type.typeJustificatif || 'requis'} est obligatoire pour ce type de frais.` });
            return;
        }

        const payload = {
            noteDeFraisId: Number(formData.noteDeFraisId),
            ordreMissionId: formData.ordreMissionId ? Number(formData.ordreMissionId) : null,
            dateDepense: formData.dateDepense,
            montantDepense: montant,
            description: formData.description,
            centreCouts: formData.centreCouts || null,
            urgent: formData.urgence,
            requestType: type.category || 'NOTE_FRAIS',
            justificatifs: formData.justificatifs,
            devise: type.devise || "MAD",
        };

        dispatch(createNoteDeFraisRequestRequest(payload));
        setShowModal(false);
        resetForm();
    };

    const showDetails = (demande) => {
        setSelectedDemande(demande);
        setShowDetailsModal(true);
    };

    return (
        <div className="pcPage">
            <div className="pcHeader">
                <div>
                    <h1>Mes notes de frais</h1>
                    <p>Déclarez vos frais professionnels et consultez vos remboursements</p>
                </div>
                <button className="pcBtn pcBtn--primary" onClick={() => setShowModal(true)}>
                    <Plus size={16} /> Nouvelle note de frais
                </button>
            </div>

            <div className="pcWrap">
                <div className="pcCard">
                    <div className="pcBody">
                        <div className="pcTopRow">
                            <h2 className="pcTitle">Historique des déclarations</h2>
                        </div>
                        
                        {loading && myRequests.length === 0 ? (
                            <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
                                <Loader size={32} className="spin" />
                            </div>
                        ) : myRequests.length === 0 ? (
                            <div className="pcEmptyCell">
                                <Receipt size={38} style={{ opacity: 0.5, marginBottom: 10 }} />
                                <div>Aucune note de frais enregistrée</div>
                            </div>
                        ) : (
                            <div className="pcTableWrap">
                                <table className="pcTable">
                                    <thead>
                                        <tr>
                                            <th>Date & Type</th>
                                            <th>Montant</th>
                                            <th>Devise</th>
                                            <th>Mission liée</th>
                                            <th>Statut</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {myRequests.map((d) => (
                                            <tr key={d.id}>
                                                <td>
                                                    <div className="pcStrong" style={{ color: getCouleurByCategorie(getRequestCategory(d)) }}>
                                                        {getRequestCategory(d)} {d.urgent && "🔥"}
                                                    </div>
                                                    <div className="pcTiny pcMuted">
                                                        {getRequestCode(d) && `${getRequestCode(d)} - `}Dépense le {formatDate(d.dateDepense)}
                                                    </div>
                                                </td>
                                                <td className="pcStrong" style={{ fontSize: 14 }}>
                                                    {formatMontant(d.montantDepense)}
                                                </td>
                                                <td>{d.devise || "MAD"}</td>
                                                <td>{d.ordreMissionLibelle || "—"}</td>
                                                <td>{getStatutBadge(d.status, d)}</td>
                                                <td>
                                                    <button className="pcBtn pcBtn--outline" style={{ padding: '4px 8px', fontSize: 11 }} onClick={() => showDetails(d)}>
                                                        Détails
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* MODAL - NOUVELLE DEMANDE */}
            {showModal && (
                <div className="pcModal">
                    <div className="pcModal__panel pcModal__panel--wide">
                        <div className="pcModal__header">
                            <div className="pcModal__headerLeft">
                                <div className="pcModal__title">Déclarer une note de frais</div>
                                <div className="pcModal__subtitle">Sélectionnez une mission et le type de dépense</div>
                            </div>
                            <button className="pcIconBtn pcIconBtn--close" onClick={() => { setShowModal(false); resetForm(); }}>
                                <X size={16} />
                            </button>
                        </div>
                        <div className="pcModal__body">
                            <form id="ndfForm" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                
                                <div className="pcWizGrid">
                                    <div className="pcWizField is-full">
                                        <label>Ordre de mission (optionnel)</label>
                                        <div className="pcSelectWrap">
                                            <select className="pcInput" value={formData.ordreMissionId} onChange={handleOrdreMissionChange}>
                                                <option value="">-- Aucune mission --</option>
                                                {ordresMissionActifs.map((om) => (
                                                    <option key={om.id} value={om.id}>
                                                        {om.code} - {om.libelle} ({formatDate(om.dateDebut)} au {formatDate(om.dateFin)})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="pcWizField is-full">
                                        <label>Type de frais <span className="pcReq">*</span></label>
                                        <div className="pcSelectWrap">
                                            <select
                                                className="pcInput"
                                                value={formData.noteDeFraisId}
                                                onChange={(e) => {
                                                    const type = typesNoteDeFraisDisponibles.find(t => t.id === Number(e.target.value));
                                                    setFormData({ ...formData, noteDeFraisId: e.target.value, category: type ? type.category : "" });
                                                }}
                                                required
                                            >
                                                <option value="">-- Sélectionner un type de frais --</option>
                                                {typesNoteDeFraisDisponibles.map((type) => (
                                                    <option key={type.id} value={type.id}>
                                                        {type.category} {type.code ? `(${type.code})` : ""}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        {typesNoteDeFraisDisponibles.length === 0 && selectedOrdreMission && (
                                            <div className="pcErrMsg" style={{ marginTop: 4 }}>
                                                Aucun type de frais n'est autorisé pour cette mission.
                                            </div>
                                        )}
                                    </div>

                                    {formData.noteDeFraisId && (
                                        <div className="pcWizField is-full">
                                            {(() => {
                                                const t = typesNoteDeFraisDisponibles.find(t => t.id === Number(formData.noteDeFraisId));
                                                if (!t) return null;
                                                return (
                                                    <div className="pcPreview" style={{ marginTop: 0 }}>
                                                        <div className="pcRecap__grid">
                                                            <div className="pcRecap__item">
                                                                <div className="pcRecap__key">Montant minimum</div>
                                                                <div className="pcRecap__val">{t.montantMinimum ? `${formatMontant(t.montantMinimum)} ${t.devise || 'MAD'}` : 'Aucun'}</div>
                                                            </div>
                                                            <div className="pcRecap__item">
                                                                <div className="pcRecap__key">Montant maximum</div>
                                                                <div className="pcRecap__val">{t.montantMaximum ? `${formatMontant(t.montantMaximum)} ${t.devise || 'MAD'}` : 'Aucun'}</div>
                                                            </div>
                                                            <div className="pcRecap__item">
                                                                <div className="pcRecap__key">Justificatif</div>
                                                                <div className="pcRecap__val">
                                                                    {t.obligatoire ? <span style={{ color: '#ef4444' }}>Obligatoire ({t.typeJustificatif})</span> : "Optionnel"}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    )}

                                    <div className="pcWizField">
                                        <label>Date de la dépense <span className="pcReq">*</span></label>
                                        <input
                                            className="pcInput"
                                            type="date"
                                            value={formData.dateDepense}
                                            onChange={(e) => setFormData({ ...formData, dateDepense: e.target.value })}
                                            min={selectedOrdreMission?.dateDebut || undefined}
                                            max={maxDateDepense}
                                            required
                                        />
                                    </div>

                                    <div className="pcWizField">
                                        <label>Montant <span className="pcReq">*</span></label>
                                        <div style={{ display: 'flex', gap: 10 }}>
                                            <input
                                                className="pcInput"
                                                style={{ flex: 1 }}
                                                type="number"
                                                value={formData.montant}
                                                onChange={(e) => setFormData({ ...formData, montant: e.target.value })}
                                                min="0"
                                                step="0.01"
                                                required
                                            />
                                            <div style={{ display: 'flex', alignItems: 'center', fontWeight: 600, color: '#64748b', background: '#f8fafc', padding: '0 12px', border: '1px solid #d1d5db', borderRadius: 8 }}>
                                                {formData.noteDeFraisId ? (typesNoteDeFraisDisponibles.find(t => t.id === Number(formData.noteDeFraisId))?.devise || "MAD") : "MAD"}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="pcWizField is-full">
                                        <label>Description <span className="pcReq">*</span></label>
                                        <textarea
                                            className="pcInput pcInput--textarea"
                                            value={formData.description}
                                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                            placeholder="Ex: Repas midi avec client X..."
                                            required
                                        />
                                    </div>
                                    
                                    <div className="pcWizField is-full">
                                        <label>
                                            Justificatifs
                                            {formData.noteDeFraisId && typesNoteDeFraisDisponibles.find(t => t.id === Number(formData.noteDeFraisId))?.obligatoire && (
                                                <span className="pcReq"> *</span>
                                            )}
                                        </label>
                                        <div style={{ border: '1px dashed #cbd5e1', padding: '20px', borderRadius: '10px', textAlign: 'center', background: '#f8fafc', position: 'relative' }}>
                                            <Upload size={24} color="#94a3b8" style={{ marginBottom: 8 }} />
                                            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 10 }}>Glissez vos fichiers ici ou cliquez pour parcourir</div>
                                            <input type="file" multiple onChange={handleFileUpload} style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }} />
                                            <button type="button" className="pcBtn pcBtn--outline" style={{ position: 'relative', pointerEvents: 'none' }}>Parcourir</button>
                                        </div>

                                        {formData.justificatifs.length > 0 && (
                                            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                {formData.justificatifs.map((file, idx) => (
                                                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f1f5f9', padding: '6px 12px', borderRadius: 6, fontSize: 12 }}>
                                                        <span>{file.name || file}</span>
                                                        <X size={14} color="#ef4444" style={{ cursor: 'pointer' }} onClick={() => removeFile(idx)} />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                </div>
                            </form>
                        </div>
                        <div className="pcModal__footer">
                            <button type="button" className="pcBtn pcBtn--ghost" onClick={() => { setShowModal(false); resetForm(); }}>
                                Annuler
                            </button>
                            <button type="submit" form="ndfForm" className="pcBtn pcBtn--primary" disabled={loading}>
                                {loading ? "Envoi..." : "Soumettre la dépense"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL - Détails */}
            {showDetailsModal && selectedDemande && (
                <div className="pcModal">
                    <div className="pcModal__panel">
                        <div className="pcModal__header">
                            <div className="pcModal__headerLeft">
                                <div className="pcModal__title">Détails de la note de frais</div>
                                <div className="pcModal__subtitle">{getRequestCategory(selectedDemande)} {getRequestCode(selectedDemande) ? `- ${getRequestCode(selectedDemande)}` : ""}</div>
                            </div>
                            <button className="pcIconBtn pcIconBtn--close" onClick={() => setShowDetailsModal(false)}>
                                <X size={16} />
                            </button>
                        </div>
                        <div className="pcModal__body" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            <div>{getStatutBadge(selectedDemande.status, selectedDemande)}</div>

                            <div className="pcRecap">
                                <div className="pcRecap__grid">
                                    <div className="pcRecap__item">
                                        <div className="pcRecap__key">Montant</div>
                                        <div className="pcRecap__val" style={{ color: '#0ea5e9', fontSize: 16 }}>{formatMontant(selectedDemande.montantDepense)} {selectedDemande.devise || "MAD"}</div>
                                    </div>
                                    <div className="pcRecap__item">
                                        <div className="pcRecap__key">Date dépense</div>
                                        <div className="pcRecap__val">{formatDate(selectedDemande.dateDepense)}</div>
                                    </div>
                                    <div className="pcRecap__item">
                                        <div className="pcRecap__key">Centre coûts</div>
                                        <div className="pcRecap__val">{selectedDemande.centreCouts || "—"}</div>
                                    </div>
                                </div>
                            </div>

                            {selectedDemande.ordreMissionId && (
                                <div className="pcInfoBox">
                                    <Briefcase size={16} color="#64748b" />
                                    <div>
                                        <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Mission liée</div>
                                        <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{selectedDemande.ordreMissionCode || "—"} - {selectedDemande.ordreMissionLibelle || "—"}</div>
                                    </div>
                                </div>
                            )}

                            <div className="pcWizField is-full">
                                <label className="pcMuted">Description</label>
                                <div style={{ fontSize: 13, color: '#374151', padding: '10px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                    {selectedDemande.description || "Aucune description"}
                                </div>
                            </div>

                            {selectedDemande.justificatifs?.length > 0 && (
                                <div className="pcWizField is-full">
                                    <label className="pcMuted">Justificatifs</label>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        {selectedDemande.justificatifs.map((f, i) => (
                                            <div key={i} style={{ fontSize: 12, padding: '8px', background: '#f1f5f9', borderRadius: 6 }}>
                                                {f}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {(selectedDemande.managerComment || selectedDemande.adminComment) && (
                                <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#ef4444', marginBottom: 8 }}>Motifs de rejet ou commentaires</div>
                                    <div style={{ fontSize: 13, background: '#fef2f2', border: '1px solid #fecaca', padding: '12px', borderRadius: 8, color: '#991b1b' }}>
                                        {selectedDemande.managerComment || selectedDemande.adminComment}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="pcModal__footer">
                            <button className="pcBtn pcBtn--ghost" onClick={() => setShowDetailsModal(false)}>Fermer</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExpenseManagement;
